import React, { useState, useEffect } from 'react';
import { numberingApi } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Hash, PencilSimple, CheckCircle, Plus, Trash, Eye, ArrowsClockwise,
  CalendarBlank, Buildings, TextAa, NumberSquareOne,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const CORE_ENTITIES = {
  uhid: { label: 'UHID', color: 'bg-sky-500', desc: 'Unique Hospital Identifier' },
  claim_no: { label: 'Claim No', color: 'bg-violet-500', desc: 'Claim Number' },
  ipd_no: { label: 'IPD No', color: 'bg-emerald-500', desc: 'In-Patient Department' },
  opd_no: { label: 'OPD No', color: 'bg-teal-500', desc: 'Out-Patient Department' },
  bill_no: { label: 'Bill No', color: 'bg-amber-500', desc: 'Bill Number' },
  receipt_no: { label: 'Receipt No', color: 'bg-rose-500', desc: 'Receipt Number' },
  preauth_no: { label: 'Pre-Auth No', color: 'bg-indigo-500', desc: 'Pre-Authorization Number' },
  appeal_no: { label: 'Appeal No', color: 'bg-orange-500', desc: 'Appeal Number' },
};

const RESET_OPTIONS = [
  { value: 'yearly', label: 'Yearly', desc: 'Resets every January 1st' },
  { value: 'monthly', label: 'Monthly', desc: 'Resets on 1st of each month' },
  { value: 'never', label: 'Never', desc: 'Continuous numbering' },
];

const generateSample = (form) => {
  const now = new Date();
  const parts = [];
  if (form.prefix) parts.push(form.prefix);
  if (form.include_year) parts.push(now.getFullYear().toString());
  if (form.include_month) parts.push(String(now.getMonth() + 1).padStart(2, '0'));
  if (form.include_branch_code) parts.push('BR01');
  parts.push(String((form.current_sequence || 0) + 1).padStart(form.padding || 5, '0'));
  if (form.suffix) parts.push(form.suffix);
  return parts.join(form.separator || '-');
};

const emptyForm = {
  entity_type: '', prefix: '', suffix: '', include_year: true, include_month: true,
  include_branch_code: false, separator: '-', padding: 5, auto_reset: 'yearly', current_sequence: 0,
};

const NumberingConfigPage = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(null);
  const [addDialog, setAddDialog] = useState(false);
  const [editForm, setEditForm] = useState({ ...emptyForm });
  const [addForm, setAddForm] = useState({ ...emptyForm });

  useEffect(() => { loadConfigs(); }, []);

  const loadConfigs = async () => {
    try {
      const res = await numberingApi.list();
      setConfigs(res.data);
    } catch { toast.error('Failed to load numbering configurations'); }
    finally { setLoading(false); }
  };

  const openEdit = (config) => {
    setEditForm({
      entity_type: config.entity_type,
      prefix: config.prefix || '',
      suffix: config.suffix || '',
      include_year: config.include_year ?? true,
      include_month: config.include_month ?? true,
      include_branch_code: config.include_branch_code ?? false,
      separator: config.separator || '-',
      padding: config.padding || 5,
      auto_reset: config.auto_reset || 'yearly',
      current_sequence: config.current_sequence || 0,
    });
    setEditDialog(config);
  };

  const saveConfig = async () => {
    try {
      await numberingApi.update(editDialog.id, editForm);
      toast.success('Configuration saved!');
      setEditDialog(null);
      loadConfigs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const addConfig = async () => {
    if (!addForm.entity_type) { toast.error('Entity type is required'); return; }
    if (!addForm.prefix) { toast.error('Prefix is required'); return; }
    try {
      await numberingApi.create(addForm);
      toast.success('Numbering configuration added!');
      setAddDialog(false);
      setAddForm({ ...emptyForm });
      loadConfigs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add'); }
  };

  const deleteConfig = async (config) => {
    const meta = CORE_ENTITIES[config.entity_type];
    if (meta) { toast.error('Core numbering types cannot be deleted'); return; }
    if (!window.confirm(`Delete numbering config "${config.entity_type}"?`)) return;
    try {
      await numberingApi.delete(config.id);
      toast.success('Deleted');
      loadConfigs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const getMeta = (entityType) => {
    return CORE_ENTITIES[entityType] || { label: entityType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), color: 'bg-slate-500', desc: 'Custom numbering' };
  };

  const ConfigForm = ({ form, setForm, onSubmit, submitLabel, showEntityType }) => (
    <div className="space-y-5">
      {/* Live Preview */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-5 text-center">
        <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">Live Preview</p>
        <p className="font-mono text-2xl font-bold text-emerald-400 tracking-wider" data-testid="numbering-preview">{generateSample(form)}</p>
        <p className="text-[10px] text-slate-500 mt-2">Next number that will be generated</p>
      </div>

      {showEntityType && (
        <div>
          <Label className="text-xs font-semibold text-slate-700">Entity Type (unique identifier) *</Label>
          <Input value={form.entity_type} onChange={e => setForm(f => ({ ...f, entity_type: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
            placeholder="e.g., lab_no, pharmacy_no, discharge_no" className="mt-1" data-testid="entity-type-input" />
          <p className="text-[10px] text-slate-400 mt-1">Use lowercase with underscores. e.g., lab_no, discharge_no</p>
        </div>
      )}

      {/* Prefix & Suffix */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1"><TextAa className="h-3.5 w-3.5" /> Prefix *</Label>
          <Input value={form.prefix} onChange={e => setForm(f => ({ ...f, prefix: e.target.value.toUpperCase() }))}
            placeholder="e.g., CLM, IPD, OPD" className="mt-1 font-mono" data-testid="numbering-prefix" />
          <p className="text-[10px] text-slate-400 mt-1">Appears at the start. Auto-uppercased.</p>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700">Suffix (optional)</Label>
          <Input value={form.suffix} onChange={e => setForm(f => ({ ...f, suffix: e.target.value }))}
            placeholder="e.g., X, A" className="mt-1 font-mono" />
          <p className="text-[10px] text-slate-400 mt-1">Appears at the end</p>
        </div>
      </div>

      {/* Separator & Padding */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-semibold text-slate-700">Separator</Label>
          <Select value={form.separator || 'none'} onValueChange={v => setForm(f => ({ ...f, separator: v === 'none' ? '' : v }))}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="-">Hyphen ( - )</SelectItem>
              <SelectItem value="/">Slash ( / )</SelectItem>
              <SelectItem value=".">Dot ( . )</SelectItem>
              <SelectItem value="none">None (concatenated)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1"><NumberSquareOne className="h-3.5 w-3.5" /> Sequence Digits</Label>
          <Select value={String(form.padding)} onValueChange={v => setForm(f => ({ ...f, padding: parseInt(v) }))}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[3, 4, 5, 6, 7, 8].map(n => <SelectItem key={n} value={String(n)}>{n} digits (e.g., {'0'.repeat(n - 1)}1)</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Reset Frequency */}
      <div>
        <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1"><ArrowsClockwise className="h-3.5 w-3.5" /> Reset Frequency</Label>
        <div className="grid grid-cols-3 gap-2 mt-2">
          {RESET_OPTIONS.map(opt => (
            <button key={opt.value} onClick={() => setForm(f => ({ ...f, auto_reset: opt.value }))}
              className={`p-2.5 rounded-lg border text-center transition-all ${form.auto_reset === opt.value
                ? 'border-sky-400 bg-sky-50 ring-1 ring-sky-200' : 'border-slate-200 hover:border-slate-300'}`}>
              <p className="text-xs font-semibold text-slate-700">{opt.label}</p>
              <p className="text-[9px] text-slate-400 mt-0.5">{opt.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Include Options */}
      <div className="bg-slate-50 rounded-lg p-4 border border-slate-100 space-y-3">
        <p className="text-xs font-semibold text-slate-700 mb-1">Include in Format</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarBlank className="h-4 w-4 text-sky-500" />
            <div><p className="text-xs font-medium text-slate-700">Year</p><p className="text-[9px] text-slate-400">e.g., 2026</p></div>
          </div>
          <Switch checked={form.include_year} onCheckedChange={v => setForm(f => ({ ...f, include_year: v }))} />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarBlank className="h-4 w-4 text-violet-500" />
            <div><p className="text-xs font-medium text-slate-700">Month</p><p className="text-[9px] text-slate-400">e.g., 04</p></div>
          </div>
          <Switch checked={form.include_month} onCheckedChange={v => setForm(f => ({ ...f, include_month: v }))} />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Buildings className="h-4 w-4 text-amber-500" />
            <div><p className="text-xs font-medium text-slate-700">Branch Code</p><p className="text-[9px] text-slate-400">e.g., BR01</p></div>
          </div>
          <Switch checked={form.include_branch_code} onCheckedChange={v => setForm(f => ({ ...f, include_branch_code: v }))} />
        </div>
      </div>

      {/* Current Sequence */}
      <div>
        <Label className="text-xs font-semibold text-slate-700">Current Sequence Start</Label>
        <Input type="number" min={0} value={form.current_sequence}
          onChange={e => setForm(f => ({ ...f, current_sequence: parseInt(e.target.value) || 0 }))}
          className="mt-1 font-mono" />
        <p className="text-[10px] text-slate-400 mt-1">Next generated number will be this + 1</p>
      </div>

      <Button className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg shadow-sky-500/25 gap-2" onClick={onSubmit} data-testid="save-numbering-btn">
        <CheckCircle className="h-4 w-4" /> {submitLabel}
      </Button>
    </div>
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500" /></div>;

  return (
    <div className="space-y-6" data-testid="numbering-config-page">
      <Toaster position="top-right" richColors />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 font-['Outfit']" data-testid="numbering-title">Numbering Configuration</h1>
          <p className="text-sm text-slate-500 mt-1">Define your hospital's auto-numbering formats for UHID, claims, bills, and more</p>
        </div>
        <Button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg shadow-sky-500/25 gap-2"
          onClick={() => { setAddForm({ ...emptyForm }); setAddDialog(true); }} data-testid="add-numbering-btn">
          <Plus className="h-4 w-4" /> Add Custom Numbering
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-white border border-slate-200 shadow-sm"><CardContent className="pt-3 pb-3 text-center">
          <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Total</p>
          <p className="text-xl font-bold text-slate-800">{configs.length}</p>
        </CardContent></Card>
        <Card className="bg-white border border-slate-200 shadow-sm"><CardContent className="pt-3 pb-3 text-center">
          <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Core</p>
          <p className="text-xl font-bold text-sky-700">{configs.filter(c => CORE_ENTITIES[c.entity_type]).length}</p>
        </CardContent></Card>
        <Card className="bg-white border border-slate-200 shadow-sm"><CardContent className="pt-3 pb-3 text-center">
          <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Custom</p>
          <p className="text-xl font-bold text-violet-700">{configs.filter(c => !CORE_ENTITIES[c.entity_type]).length}</p>
        </CardContent></Card>
      </div>

      {/* Config Table List */}
      {configs.length === 0 ? (
        <Card className="bg-white border border-slate-200">
          <CardContent className="text-center py-12">
            <Hash className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">No numbering configurations found. They are auto-created when your hospital is registered.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border border-slate-200 shadow-sm" data-testid="numbering-table">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Type</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Prefix</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Sample / Next Number</th>
                    <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Separator</th>
                    <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Digits</th>
                    <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Reset</th>
                    <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Includes</th>
                    <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {configs.map((config) => {
                    const meta = getMeta(config.entity_type);
                    const isCore = !!CORE_ENTITIES[config.entity_type];
                    const sepLabel = { '-': 'Hyphen', '/': 'Slash', '.': 'Dot' }[config.separator] || 'None';
                    return (
                      <tr key={config.id || config.entity_type}
                        className="border-b border-slate-100 hover:bg-sky-50/40 transition-colors group"
                        data-testid={`numbering-row-${config.entity_type}`}>
                        {/* Type */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className={`${meta.color} h-8 w-8 rounded-lg flex items-center justify-center text-white text-[10px] font-bold shrink-0`}>
                              {meta.label.substring(0, 2)}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-slate-800">{meta.label}</p>
                              <p className="text-[10px] text-slate-400">{meta.desc}</p>
                            </div>
                            {!isCore && <Badge className="bg-emerald-50 text-emerald-600 border-0 text-[8px] ml-1">Custom</Badge>}
                          </div>
                        </td>
                        {/* Prefix */}
                        <td className="px-4 py-3">
                          <code className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-xs font-mono font-semibold">{config.prefix || '-'}</code>
                          {config.suffix && <code className="bg-rose-50 text-rose-600 px-1.5 py-0.5 rounded text-[10px] font-mono ml-1">+{config.suffix}</code>}
                        </td>
                        {/* Sample */}
                        <td className="px-4 py-3">
                          <code className="bg-slate-900 text-emerald-400 px-3 py-1 rounded-md text-xs font-mono font-bold inline-block"
                            data-testid={`sample-${config.entity_type}`}>
                            {config.sample_format || '-'}
                          </code>
                        </td>
                        {/* Separator */}
                        <td className="px-4 py-3 text-center">
                          <span className="text-xs text-slate-600">{sepLabel}</span>
                        </td>
                        {/* Digits */}
                        <td className="px-4 py-3 text-center">
                          <span className="text-xs font-medium text-slate-700">{config.padding}</span>
                        </td>
                        {/* Reset */}
                        <td className="px-4 py-3 text-center">
                          <Badge className={`border-0 text-[10px] ${
                            config.auto_reset === 'yearly' ? 'bg-sky-100 text-sky-700' :
                            config.auto_reset === 'monthly' ? 'bg-violet-100 text-violet-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>{config.auto_reset}</Badge>
                        </td>
                        {/* Includes */}
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {config.include_year && <span className="px-1.5 py-0.5 rounded bg-sky-50 text-[9px] font-medium text-sky-600">Y</span>}
                            {config.include_month && <span className="px-1.5 py-0.5 rounded bg-violet-50 text-[9px] font-medium text-violet-600">M</span>}
                            {config.include_branch_code && <span className="px-1.5 py-0.5 rounded bg-amber-50 text-[9px] font-medium text-amber-600">BR</span>}
                            {!config.include_year && !config.include_month && !config.include_branch_code && <span className="text-xs text-slate-300">-</span>}
                          </div>
                        </td>
                        {/* Actions */}
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-0.5">
                            <Button variant="ghost" size="sm" onClick={() => openEdit(config)}
                              className="text-sky-600 hover:bg-sky-50 h-7 w-7 p-0"
                              data-testid={`edit-numbering-${config.entity_type}`}>
                              <PencilSimple className="h-3.5 w-3.5" />
                            </Button>
                            {!isCore && (
                              <Button variant="ghost" size="sm" onClick={() => deleteConfig(config)}
                                className="text-slate-400 hover:text-red-500 hover:bg-red-50 h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`delete-numbering-${config.entity_type}`}>
                                <Trash className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Edit {getMeta(editForm.entity_type)?.label} Format</DialogTitle>
            <DialogDescription>Customize how {getMeta(editForm.entity_type)?.label} numbers are generated for your hospital.</DialogDescription>
          </DialogHeader>
          <ConfigForm form={editForm} setForm={setEditForm} onSubmit={saveConfig} submitLabel="Save Configuration" showEntityType={false} />
        </DialogContent>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Add Custom Numbering</DialogTitle>
            <DialogDescription>Create a new numbering format for your hospital (e.g., Lab No, Pharmacy No, Discharge No).</DialogDescription>
          </DialogHeader>
          <ConfigForm form={addForm} setForm={setAddForm} onSubmit={addConfig} submitLabel="Add Configuration" showEntityType={true} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NumberingConfigPage;
